import CreateButton from "../components/CreateButton"
import { useEffect, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useNavigate } from "react-router-dom"
import api from "../api"
import Badge from "../components/Badge"
import AlertDialog from "../components/dialogs/AlertDialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import typo from "../typography.module.css"
import { formatNumber } from "../utils"
import styles from "./Surveys.module.css"

const Downloads = () => {
    const [downloads, setDownloads] = useState([])
    const [loading, setLoading] = useState(false)
    const [downloadToDelete, setDownloadToDelete] = useState(null)
    const navigate = useNavigate()

    const getDownloads = async (q = null) => {
        setLoading(true)
        try {
            const data = q && q.length > 0
                ? await api.get(`/admin/downloads?q=${encodeURIComponent(q)}`)
                : await api.get(`/admin/downloads?page=0&per=200`)
            setDownloads(data.downloads || [])
        } catch (e) {
            console.error(e)
        }
        setLoading(false)
    }

    useEffect(() => {
        getDownloads()
    }, [])

    const getStatusBadge = (status) => {
        switch (status) {
            case 'public':
                return <Badge size="small" name="PUBBLICO" color="var(--sf-green)" />
            case 'hidden':
                return <Badge size="small" name="NASCOSTO" color="var(--sf-red)" />
            default:
                return <Badge size="small" name="BOZZA" color="var(--secondary)" />
        }
    }

    const deleteDownload = async (downloadId) => {
        try {
            await api.delete(`/admin/downloads/${downloadId}`)
            await getDownloads()
            setDownloadToDelete(null)
        } catch (e) {
            console.error(e)
        }
    }

    const copyDownloadLink = async (download) => {
        if (!download?.slug) {
            return
        }

        try {
            await navigator.clipboard.writeText(`${process.env.REACT_APP_WEBAPP_URL}/downloads/${download.slug}`)
        } catch (e) {
            console.error(e)
        }
    }

    const exportDownloadersCsv = async (download) => {
        if (!download?.id) {
            return
        }

        try {
            const blob = await api.get(`/admin/downloads/${download.id}/downloaders/csv`, {
                responseType: 'blob',
            })

            const fileUrl = window.URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            const safeSlug = download.slug || `download_${download.id}`
            anchor.href = fileUrl
            anchor.download = `${safeSlug}_downloaders.csv`
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()
            window.URL.revokeObjectURL(fileUrl)
        } catch (e) {
            console.error(e)
        }
    }

    return (
        <HeaderFooterLayout>
            <AlertDialog
                open={!!downloadToDelete}
                title="Eliminare il download?"
                text={`Sei sicuro di voler eliminare il file <strong>${downloadToDelete?.name || ''}</strong>? Questa operazione non puo essere annullata.`}
                onClose={() => setDownloadToDelete(null)}
                actions={[
                    {
                        label: 'ANNULLA',
                        inverse: true,
                        onClick: async () => setDownloadToDelete(null),
                    },
                    {
                        label: 'ELIMINA',
                        accentColor: 'var(--sf-red)',
                        onClick: async () => {
                            if (!downloadToDelete?.id) {
                                setDownloadToDelete(null)
                                return
                            }
                            await deleteDownload(downloadToDelete.id)
                        },
                    },
                ]}
            />
            <HelmetProvider>
                <Helmet>
                    <title>Contenuti - Downloads</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <div className={styles.header}>
                            <div className={typo.title}>Downloads</div>
                            <CreateButton onClick={() => { navigate('/downloads/create/editor') }}>
                                Aggiungi

                            </CreateButton>
                        </div>

                        <TextInput
                            onKeyUp={(value) => getDownloads(value?.toLowerCase?.() || value)}
                            type="search"
                            placeholder="Cerca"
                        />

                        <div className={styles.surveys}>
                            <Table
                                appearance="minimal"
                                loading={loading}
                                loadingN={5}
                                style={{ backgroundColor: 'var(--background-color)' }}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'number' },
                                    { key: 'created_at', label: 'CREATO IL', type: 'date' },
                                    { key: 'name', label: 'NOME', type: 'string' },
                                    { key: 'slug', label: 'SLUG', type: 'string' },
                                    { key: 'page_views', label: 'VISUALIZZAZIONI', type: 'custom' },
                                    { key: 'downloads', label: 'DOWNLOAD', type: 'custom' },
                                    { key: 'status', label: 'STATO', type: 'custom' },
                                    { key: 'actions', label: 'AZIONI', type: 'custom' },
                                ]}
                                elements={downloads.map((download) => ({
                                    ...download,
                                    href: `/downloads/${download.id}/editor`,
                                    page_views: { value: download.page_views || 0, type: 'number', component: formatNumber(download.page_views || 0) },
                                    downloads: { value: download.downloads || 0, type: 'number', component: formatNumber(download.downloads || 0) },
                                    status: { value: download.status, type: 'string', component: getStatusBadge(download.status) },
                                    actions: {
                                        value: 0,
                                        type: 'number',
                                        component: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                                                <MeatBallsMenu
                                                    actions={[
                                                        {
                                                            label: 'Copia Link',
                                                            onClick: () => copyDownloadLink(download),
                                                        },
                                                        {
                                                            label: 'Esporta CSV utenti',
                                                            onClick: () => exportDownloadersCsv(download),
                                                        },
                                                        {
                                                            label: 'Elimina',
                                                            style: { color: 'var(--sf-red)' },
                                                            onClick: () => setDownloadToDelete(download),
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        ),
                                    },
                                }))}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </HeaderFooterLayout>
    )
}

export default Downloads
